import { h } from "vue";
import { useModal } from "vue-final-modal";

import type { ModalFormat } from "@/components/modals/Modal.vue";
import { browser } from "@/services/browser";
import UpgradeBlockedModal from "@/upgrade/UpgradeBlockedModal.vue";
import type { UpgradeBlockedReason } from "@/upgrade/upgrader";
import { openExternalUrl } from "@/utils/open-external-url";
import { property } from "@/utils/unknown";

export type UpgradeBlockedEvent = {
  reason: UpgradeBlockedReason;
  /** Whether the user asked for the update check themselves. */
  manual: boolean;
};

/** Parse the payload of the main process's "upgrade-blocked" IPC event. */
export const parseUpgradeBlockedEvent = (
  value: unknown
): UpgradeBlockedEvent | undefined => {
  if (typeof value !== "object" || value === null) return undefined;
  const reason = property(value, "reason");
  if (
    reason !== "read-only-volume" &&
    reason !== "process-blocked" &&
    reason !== "updater-missing"
  ) {
    return undefined;
  }
  return { reason, manual: property(value, "manual") === true };
};

// Automatic checks (startup, hourly) only show the modal a few times in
// total — the condition is persistent and the reminder gets old — while
// manual checks always show it, since the user asked what happened.
const MAX_AUTOMATIC_DISPLAYS = 3;

const storageKey = (reason: UpgradeBlockedReason) => `upgradeFails:${reason}`;

const automaticDisplaysExhausted = (reason: UpgradeBlockedReason): boolean => {
  const raw: unknown = browser.store.getJson(storageKey(reason), 0);
  const displays = typeof raw === "number" ? raw : 0;
  if (displays >= MAX_AUTOMATIC_DISPLAYS) return true;
  browser.store.setJson(storageKey(reason), displays + 1);
  return false;
};

// One modal at a time: the main process resends the blocked state on
// renderer handshake and on manual checks, which may repeat the reason
// while the modal is already up.
let isOpen = false;

export const openUpgradeBlockedModal = async ({
  reason,
  manual,
}: UpgradeBlockedEvent) => {
  if (isOpen) return;
  if (!manual && automaticDisplaysExhausted(reason)) return;
  isOpen = true;
  // Imported lazily so this module stays safe to import from App.vue:
  // Modal.vue sits in a cyclic import graph (inject.ts → pickers → other
  // modals → Modal.vue) that must not be evaluated from the app's first
  // module, or components in the cycle hit their imports' temporal dead
  // zone ("Cannot access 'Modal' before initialization").
  const { default: Modal } = await import("@/components/modals/Modal.vue");
  const { open } = useModal({
    component: Modal,
    attrs: {
      title: "Updates unavailable",
      format: "warning" satisfies ModalFormat,
      isCancellable: true,
      showTitleBar: false,
      showCancelButton: false,
      modalButtons: [
        ...(reason === "process-blocked" || reason === "updater-missing"
          ? [
              {
                title: "Download",
                kind: "main" as const,
                type: "secondary" as const,
                func: async () => {
                  await openExternalUrl(
                    "https://routine.co/download",
                    null,
                    false
                  );
                  return false;
                },
              },
            ]
          : []),
        {
          title: "Got it",
          kind: "main" as const,
          type: "primary" as const,
          func: async () => true,
        },
      ],
      afterClosed: () => {
        isOpen = false;
      },
    },
    slots: {
      default: () => h(UpgradeBlockedModal, { reason }),
    },
  });
  open();
};
